import React, { useState, useEffect } from 'react';
import { X, MapPin, Search, Navigation, Wallet, CreditCard, Banknote, Star, Check, ArrowRight } from 'lucide-react';
import { SAVED_PLACES, RIDE_TYPES, DRIVERS } from '../mock';

export default function BookRideModal({ open, onClose }) {
  const [step, setStep] = useState(1); // 1=places, 2=ride select, 3=searching, 4=on-the-way
  const [pickup, setPickup] = useState('Current location');
  const [drop, setDrop] = useState('');
  const [search, setSearch] = useState('');
  const [selectedRide, setSelectedRide] = useState('mini');
  const [payment, setPayment] = useState('upi');
  const [driver, setDriver] = useState(null);

  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setStep(1); setDrop(''); setSearch(''); setSelectedRide('mini'); setPayment('upi'); setDriver(null);
      }, 250);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (step === 3) {
      const t = setTimeout(() => {
        setDriver(DRIVERS[Math.floor(Math.random() * DRIVERS.length)]);
        setStep(4);
      }, 2200);
      return () => clearTimeout(t);
    }
  }, [step]);

  if (!open) return null;

  const filteredPlaces = SAVED_PLACES.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.address.toLowerCase().includes(search.toLowerCase())
  );

  const ride = RIDE_TYPES.find(r => r.id === selectedRide);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-[fadeIn_0.2s_ease]">
      <div className="absolute inset-0 bg-black/65 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-[480px] bg-white rounded-3xl shadow-2xl overflow-hidden animate-[slideUp_0.25s_ease]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-black/5">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#0f0f10] text-white text-[9px] font-bold tracking-wider">OTW</span>
            <span className="text-[14px] font-semibold tracking-[0.16em] text-[#0f0f10]">BOOK A RIDE</span>
          </div>
          <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-full hover:bg-black/5 transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Step 1: Pick destination */}
        {step === 1 && (
          <div className="p-5">
            <div className="space-y-2">
              <div className="flex items-center gap-3 bg-[#f7f7f5] rounded-xl px-4 py-3">
                <span className="w-2.5 h-2.5 rounded-full bg-[#FF6A1A]" />
                <input value={pickup} onChange={e=>setPickup(e.target.value)} className="flex-1 bg-transparent outline-none text-[14.5px] text-[#0f0f10]" />
              </div>
              <div className="flex items-center gap-3 bg-[#f7f7f5] rounded-xl px-4 py-3 ring-2 ring-[#FF6A1A]/30">
                <span className="w-2.5 h-2.5 rounded-sm bg-[#0f0f10]" />
                <input
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Where to?"
                  className="flex-1 bg-transparent outline-none text-[14.5px] text-[#0f0f10] placeholder:text-[#a3a3a6]"
                />
                <Search size={16} className="text-[#6b6b70]" />
              </div>
            </div>

            <div className="mt-5 text-[10.5px] font-semibold tracking-[0.18em] text-[#6b6b70]">SAVED & POPULAR</div>
            <ul className="mt-2 max-h-[300px] overflow-y-auto -mx-1">
              {filteredPlaces.map((p) => (
                <li key={p.name}>
                  <button
                    onClick={() => { setDrop(p.name); setStep(2); }}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-[#f7f7f5] transition-colors text-left"
                  >
                    <div className="w-9 h-9 rounded-lg bg-[#f7f7f5] grid place-items-center text-[#0f0f10] flex-shrink-0">
                      <MapPin size={16} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[14px] font-semibold text-[#0f0f10] truncate">{p.name}</div>
                      <div className="text-[12px] text-[#6b6b70] truncate">{p.address}</div>
                    </div>
                  </button>
                </li>
              ))}
              {filteredPlaces.length === 0 && (
                <li className="px-3 py-6 text-center text-[13px] text-[#6b6b70]">No matches — try another address.</li>
              )}
            </ul>
          </div>
        )}

        {/* Step 2: Choose ride */}
        {step === 2 && (
          <div className="p-5">
            <div className="flex items-center gap-2 text-[12.5px] text-[#6b6b70]">
              <span className="truncate">{pickup}</span>
              <ArrowRight size={13} />
              <span className="font-semibold text-[#0f0f10] truncate">{drop}</span>
            </div>

            <div className="mt-4 max-h-[320px] overflow-y-auto -mx-1">
              {RIDE_TYPES.map((r) => {
                const I = r.icon;
                const active = selectedRide === r.id;
                return (
                  <button
                    key={r.id}
                    onClick={() => setSelectedRide(r.id)}
                    className={`w-full flex items-center gap-4 px-3 py-3 rounded-xl mb-1 transition-colors text-left ${
                      active ? 'bg-[#FFF1E6] ring-2 ring-[#FF6A1A]/40' : 'hover:bg-[#f7f7f5]'
                    }`}
                  >
                    <div className={`w-11 h-11 rounded-xl grid place-items-center ${active ? 'bg-[#FF6A1A] text-white' : 'bg-[#f7f7f5] text-[#0f0f10]'}`}>
                      <I size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[14.5px] font-semibold text-[#0f0f10] flex items-center gap-2">
                        {r.label}
                        <span className="text-[11px] font-medium text-[#6b6b70]">· {r.eta}</span>
                      </div>
                      <div className="text-[12px] text-[#6b6b70]">{r.seats}</div>
                    </div>
                    <div className="text-[15px] font-semibold text-[#0f0f10]">₹{r.price}</div>
                  </button>
                );
              })}
            </div>

            <div className="mt-4 grid grid-cols-3 gap-2">
              {[
                { id: 'upi', label: 'UPI', icon: Wallet },
                { id: 'card', label: 'Card', icon: CreditCard },
                { id: 'cash', label: 'Cash', icon: Banknote },
              ].map((p) => {
                const I = p.icon;
                const active = payment === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => setPayment(p.id)}
                    className={`flex items-center justify-center gap-2 rounded-xl py-2.5 text-[12.5px] font-semibold transition-colors ${
                      active ? 'bg-[#0f0f10] text-white' : 'bg-[#f7f7f5] text-[#0f0f10] hover:bg-[#eeefe9]'
                    }`}
                  >
                    <I size={14} /> {p.label}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setStep(3)}
              className="mt-4 w-full bg-[#FF6A1A] hover:bg-[#ff5a05] text-white font-semibold rounded-full py-3.5 text-[15px] transition-colors"
            >
              Confirm ₹{ride.price} {ride.label}
            </button>
          </div>
        )}

        {/* Step 3: Searching */}
        {step === 3 && (
          <div className="p-10 text-center">
            <div className="relative w-20 h-20 mx-auto">
              <div className="absolute inset-0 rounded-full border-4 border-[#FF6A1A]/20" />
              <div className="absolute inset-0 rounded-full border-4 border-[#FF6A1A] border-t-transparent animate-spin" />
              <div className="absolute inset-0 grid place-items-center">
                <Navigation size={26} className="text-[#FF6A1A] -rotate-12" />
              </div>
            </div>
            <div className="mt-6 text-[17px] font-semibold text-[#0f0f10]">Looking for nearby drivers</div>
            <div className="mt-1.5 text-[13px] text-[#6b6b70]">Matching you with a verified {ride.label.toLowerCase()} driver…</div>
          </div>
        )}

        {/* Step 4: On the way */}
        {step === 4 && driver && (
          <div className="p-5">
            <div className="rounded-2xl bg-[#EAF8EE] p-3.5 flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-[#0E8A3F] text-white grid place-items-center flex-shrink-0">
                <Check size={18} strokeWidth={3} />
              </div>
              <div>
                <div className="text-[14px] font-semibold text-[#0f0f10]">Driver assigned</div>
                <div className="text-[12px] text-[#0E8A3F] font-semibold">Arriving in 2 minutes</div>
              </div>
            </div>

            <div className="mt-4 rounded-2xl border border-black/[0.06] p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#FFE5D2] text-[#FF6A1A] grid place-items-center font-bold text-[16px] flex-shrink-0">
                  {driver.name.charAt(0)}
                </div>
                <div className="flex-1">
                  <div className="text-[15px] font-semibold text-[#0f0f10]">{driver.name}</div>
                  <div className="text-[12px] text-[#6b6b70] flex items-center gap-1.5">
                    <Star size={11} className="fill-[#FFB400] stroke-[#FFB400]" />
                    {driver.rating} · {driver.car}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10.5px] font-semibold tracking-[0.16em] text-[#6b6b70]">PLATE</div>
                  <div className="text-[13px] font-mono font-semibold text-[#0f0f10]">{driver.plate}</div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-black/[0.06] grid grid-cols-3 gap-3 text-center">
                <div>
                  <div className="text-[10px] font-semibold tracking-[0.16em] text-[#6b6b70]">FARE</div>
                  <div className="text-[15px] font-semibold text-[#0f0f10] mt-1">₹{ride.price}</div>
                </div>
                <div>
                  <div className="text-[10px] font-semibold tracking-[0.16em] text-[#6b6b70]">PAYMENT</div>
                  <div className="text-[15px] font-semibold text-[#0f0f10] mt-1 uppercase">{payment}</div>
                </div>
                <div>
                  <div className="text-[10px] font-semibold tracking-[0.16em] text-[#6b6b70]">OTP</div>
                  <div className="text-[15px] font-mono font-semibold text-[#FF6A1A] mt-1">{Math.floor(1000 + Math.random()*9000)}</div>
                </div>
              </div>
            </div>

            <div className="mt-3 text-[12px] text-[#6b6b70] text-center leading-relaxed">
              From {pickup} → <span className="font-semibold text-[#0f0f10]">{drop}</span>
            </div>

            <button
              onClick={onClose}
              className="mt-4 w-full bg-[#0f0f10] hover:bg-[#222] text-white font-semibold rounded-full py-3.5 text-[15px] transition-colors"
            >
              On the way
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
